import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type {
  CollectionMethod,
  CreateReturnResponse,
  LookupResponse,
  ResolutionType,
  ReturnReasonCode,
} from "@sirdab/returns-shared";

import { FLOW_STEPS, type FlowStepId } from "./steps";

/**
 * The wizard's working state.
 *
 * IN MEMORY ONLY. Order contents never touch `localStorage` (apps/portal/AGENTS.md) — a
 * shared phone must not leak one shopper's order to the next person who opens the browser.
 * A reload legitimately loses the draft and returns to lookup; that is the correct trade.
 */

export interface FlowLineDraft {
  lineRef: string;
  qty: number;
  reasonCode: ReturnReasonCode | null;
  subReason: string | null;
  note: string | null;
  /** `uploadId`s minted by the shopper uploads endpoint, not the files themselves. */
  uploadIds: string[];
}

export interface FlowState {
  step: FlowStepId;
  merchantSlug: string;
  orderRef: string;
  phone: string;
  /**
   * The bearer minted by the one-time code, when the shopper signed in with a phone
   * rather than an order number. Memory only: it dies with the tab, like the draft.
   */
  shopperToken: string | null;
  lookup: LookupResponse | null;
  lines: FlowLineDraft[];
  resolution: ResolutionType | null;
  collectionMethod: CollectionMethod | null;
  submitted: CreateReturnResponse | null;
}

export interface FlowContextValue extends FlowState {
  goTo: (step: FlowStepId) => void;
  next: () => void;
  back: () => void;
  update: (patch: Partial<Omit<FlowState, "step">>) => void;
  /** Wipes every trace of the order from memory. Called on confirmation and on abandon. */
  reset: () => void;
  stepIndex: number;
  stepCount: number;
}

const FlowContext = createContext<FlowContextValue | null>(null);

function emptyState(merchantSlug: string): FlowState {
  return {
    step: "lookup",
    merchantSlug,
    orderRef: "",
    phone: "",
    shopperToken: null,
    lookup: null,
    lines: [],
    resolution: null,
    collectionMethod: null,
    submitted: null,
  };
}

export function FlowProvider({
  merchantSlug,
  initialStep,
  children,
}: {
  merchantSlug: string;
  initialStep?: FlowStepId | undefined;
  children: ReactNode;
}): React.JSX.Element {
  const [state, setState] = useState<FlowState>(() => ({
    ...emptyState(merchantSlug),
    ...(initialStep !== undefined ? { step: initialStep } : {}),
  }));

  const goTo = useCallback((step: FlowStepId) => {
    setState((prev) => ({ ...prev, step }));
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }, []);

  const move = useCallback(
    (delta: number) =>
      setState((prev) => {
        const i = FLOW_STEPS.indexOf(prev.step);
        const next =
          FLOW_STEPS[Math.min(Math.max(i + delta, 0), FLOW_STEPS.length - 1)];
        return next === undefined ? prev : { ...prev, step: next };
      }),
    [],
  );

  const value = useMemo<FlowContextValue>(
    () => ({
      ...state,
      goTo,
      next: () => move(1),
      back: () => move(-1),
      update: (patch) => setState((prev) => ({ ...prev, ...patch })),
      reset: () => setState(emptyState(merchantSlug)),
      stepIndex: FLOW_STEPS.indexOf(state.step) + 1,
      stepCount: FLOW_STEPS.length,
    }),
    [state, goTo, move, merchantSlug],
  );

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}

export function useFlow(): FlowContextValue {
  const value = useContext(FlowContext);
  if (value === null)
    throw new Error("useFlow must be used inside <FlowProvider>");
  return value;
}
